import { inject, Injectable } from '@angular/core';
import { ANONYMIZATION_CONFIG, AnonymizationConfig } from '../configs/anonymization.config';
import { Reasoning } from '../interaction/interaction.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AnonymizationService {
  private readonly authService = inject(AuthService);
  private readonly config: AnonymizationConfig = { ...ANONYMIZATION_CONFIG };

  private compiledDictionaryRegex: RegExp | null = null;
  private compiledLookup: Map<string, string> | null = null;

  private compiledReverseRegex: RegExp | null = null;
  private compiledReverseLookup: Map<string, string> | null = null;

  // Regex per intercettare numeri (formato italiano, standard, con o senza decimali e separatori delle migliaia),
  // escludendo date (YYYY-MM-DD o DD/MM/YYYY), percorsi (/api/v1), citazioni ([1]) o identificatori alfanumerici.
  private readonly numberRegex =
    /(?<![-/\\#@\w[\]])\d+(?:[.,]\d+)*(?![-/\\#@\w[\]])/g;

  /**
   * Verifica se l'utente corrente è quello target per cui anonimizzare la risposta (mario.rossi@telecomitalia.it).
   */
  public isTargetUser(): boolean {
    const currentEmail = this.authService.getUserEmail();
    if (!currentEmail) {
      return false;
    }
    return (
      currentEmail.trim().toLowerCase() ===
      this.config.targetUserEmail.trim().toLowerCase()
    );
  }

  /**
   * Restituisce il divisore da utilizzare:
   * se in modalità 'fixed', restituisce sempre il divisore fisso (es. 3.14).
   * Altrimenti genera un valore random nel range configurato.
   */
  public getRandomDivisor(min = this.config.divisorRange.min, max = this.config.divisorRange.max): number {
    if (this.config.divisorMode === 'fixed') {
      return this.config.fixedDivisor ?? 3.14;
    }

    if (this.config.divisorMode === 'integer') {
      const generated = Math.floor(Math.random() * (max - min + 1)) + min;
      // Se il divisore generato è 1, per evitare che i numeri restino identici garantiamo una modifica (es. 2)
      return generated === 1 && max > 1 ? 2 : generated;
    }

    // Modalità float: genera un numero con 2 cifre decimali tra min (o 1.1 per garantire variazione) e max
    const effectiveMin = min <= 1 ? 1.2 : min;
    const val = Math.random() * (max - effectiveMin) + effectiveMin;
    return Number(val.toFixed(2));
  }

  /**
   * Anonimizza una stringa testuale:
   * 1. Divide tutti i numeri intercettati per il divisore casuale
   * 2. Sostituisce le parole sensibili predisposte (es. 'tiaky')
   */
  public anonymizeText(text: string, divisor?: number): string {
    if (!text || !this.isTargetUser()) {
      return text;
    }

    const currentDivisor = divisor ?? this.getRandomDivisor();

    // 1. Sostituzione parole configurate (es. 'tiaky')
    let processedText = this.replaceWords(text);

    // 2. Intercettazione e divisione dei numeri
    processedText = this.divideNumbers(processedText, currentDivisor);

    return processedText;
  }

  /**
   * Reverte solo la stringa (nomi, parole ed entità anonimizzate come 'hyperion' -> 'tiaky')
   * senza toccare i numeri, da utilizzare in fase di invio richiesta verso il backend.
   */
  public deanonymizeText(text: string): string {
    if (!text || !this.isTargetUser()) {
      return text;
    }

    if (!this.compiledReverseRegex || !this.compiledReverseLookup) {
      this.initDictionaryRegex();
    }

    let result = text;

    // 1. Revert prioritario per 'hyperion-arc' e 'hyperion' preservando il casing
    result = result.replaceAll(/\bhyperion-arc\b/gi, (match) => {
      if (match === match.toUpperCase()) return 'TIAKY-ARC';
      if (match.startsWith('H')) return 'Tiaky-Arc';
      return 'tiaky-arc';
    });

    result = result.replaceAll(/\bhyperion\b/gi, (match) => {
      if (match === match.toUpperCase()) return 'TIAKY';
      if (match.startsWith('H')) return 'Tiaky';
      return 'tiaky';
    });

    // 2. Revert regex per codici strutturati (es. APP-TP-323 -> BUTP323)
    result = result.replaceAll(/\bAPP-([A-Z]{2})-(\d{3})\b/g, 'BU$1$2');

    // 3. Revert lookup dal dizionario inverso (senza toccare alcun numero)
    if (this.compiledReverseRegex && this.compiledReverseLookup) {
      result = result.replaceAll(this.compiledReverseRegex, (match) => {
        return this.compiledReverseLookup!.get(match.toLowerCase()) || match;
      });
    }

    return result;
  }

  /**
   * Anonimizza l'oggetto Reasoning (sia step intermedi di reasoning che final_response).
   */
  public anonymizeReasoning(reasoning: Reasoning, divisor?: number): Reasoning {
    if (!reasoning || !this.isTargetUser()) {
      return reasoning;
    }

    const currentDivisor = divisor ?? this.getRandomDivisor();

    const anonymizedMessages = Array.isArray(reasoning.messages)
      ? reasoning.messages.map((msg) => this.anonymizeText(msg, currentDivisor))
      : reasoning.messages;

    const clonedReasoning: Reasoning = {
      ...reasoning,
      messages: anonymizedMessages,
    };

    // Anonimizza anche eventuali dati grafici in additional_info se presenti
    if (Array.isArray(clonedReasoning.additional_info)) {
      clonedReasoning.additional_info = clonedReasoning.additional_info.map((info: any) => {
        if (!info) return info;
        const updated = { ...info };
        if (Array.isArray(updated.values)) {
          updated.values = updated.values.map((v: number) =>
            typeof v === 'number' ? Number((v / currentDivisor).toFixed(2)) : v
          );
        }
        if (Array.isArray(updated.labels)) {
          updated.labels = updated.labels.map((l: string) =>
            typeof l === 'string' ? this.replaceWords(l) : l
          );
        }
        return updated;
      });
    }

    return clonedReasoning;
  }

  /**
   * Popola la mappa di lookup per le sostituzioni e il relativo reverse lookup.
   */
  private buildDictionaryMaps(): { lookup: Map<string, string>; reverseLookup: Map<string, string> } {
    const lookup = new Map<string, string>();
    const reverseLookup = new Map<string, string>();

    reverseLookup.set('hyperion', 'tiaky');
    reverseLookup.set('hyperion-arc', 'tiaky-arc');

    const wordsMap: Record<string, string> = { ...this.config.wordsToReplace };
    for (const word of this.config.wordList) {
      if (!wordsMap[word]) {
        wordsMap[word] = this.config.defaultWordReplacement;
      }
    }

    for (const [key, val] of Object.entries(wordsMap)) {
      if (key?.trim()) {
        const cleanKey = key.trim().toLowerCase();
        lookup.set(cleanKey, val);

        if (val?.trim()) {
          const cleanVal = val.trim().toLowerCase();
          if (!reverseLookup.has(cleanVal) && cleanVal !== cleanKey) {
            reverseLookup.set(cleanVal, key.trim());
          }
        }
      }
    }

    return { lookup, reverseLookup };
  }

  /**
   * Compila una regex di sostituzione a partire dalle chiavi di una mappa.
   */
  private compileMapRegex(map: Map<string, string>): RegExp | null {
    const sortedKeys = Array.from(map.keys()).sort((a, b) => b.length - a.length);
    const escaped = sortedKeys.map((k) => k.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`));
    if (escaped.length === 0) {
      return null;
    }
    return new RegExp(`(?:^|(?<=[^a-zA-Z0-9_]))(?:${escaped.join('|')})(?=[^a-zA-Z0-9_]|$)`, 'gi');
  }

  /**
   * Inizializza la regex compilata e la mappa di lookup per le sostituzioni del dizionario.
   */
  private initDictionaryRegex(): void {
    const { lookup, reverseLookup } = this.buildDictionaryMaps();
    this.compiledLookup = lookup;
    this.compiledReverseLookup = reverseLookup;
    this.compiledDictionaryRegex = this.compileMapRegex(lookup);
    this.compiledReverseRegex = this.compileMapRegex(reverseLookup);
  }

  /**
   * Sostituisce le parole e le entità configurate con corrispettivi realistici immaginari (senza asterischi).
   */
  private replaceWords(text: string): string {
    if (!this.compiledDictionaryRegex || !this.compiledLookup) {
      this.initDictionaryRegex();
    }

    let result = text;

    // Regola per preservare il casing di 'tiaki-arc' e 'tiaky-arc'
    result = result.replaceAll(/\btiak[iy]-arc\b/gi, (match) => {
      if (match === match.toUpperCase()) return 'HYPERION-ARC';
      if (match.startsWith('T')) return 'Hyperion-Arc';
      return 'hyperion-arc';
    });

    if (this.compiledDictionaryRegex && this.compiledLookup) {
      result = result.replaceAll(this.compiledDictionaryRegex, (match) => {
        return this.compiledLookup!.get(match.toLowerCase()) || match;
      });
    }

    // Regole regex per codici applicativi cliente e CI (es. BUTP323 -> APP-TP-323)
    result = result.replaceAll(/\bBU([A-Z]{2})(\d{3})\b/g, 'APP-$1-$2');

    // Regole regex per codici ODL (es. 5CUFST5003 -> 7CUSYS5003)
    result = result.replaceAll(/\b\d([A-Z]{2})[A-Z]{3}(\d{4})\b/g, '7$1SYS$2');

    // Regole regex per codici WBS / CDC (es. FSTH.4478 -> INVT.4478)
    result = result.replaceAll(/\b(FSTH|ITF|FS01|FSE|ITAL)\.([0-9A-Za-z._-]+)\b/g, 'INVT.$2');

    // Regola di fallback per preservare il casing di 'tiaky' e 'tiaki'
    result = result.replaceAll(/\btiak[iy]\b/gi, (match) => {
      if (match === match.toUpperCase()) return 'HYPERION';
      if (match.startsWith('T')) return 'Hyperion';
      return 'hyperion';
    });

    return result;
  }

  /**
   * Intercetta e divide esclusivamente i valori economici in € (sia in tabelle Markdown con colonna in € sia nel testo inline).
   * Tutti gli altri numeri (conteggi, quantità, percentuali, date, ID) rimangono inalterati.
   */
  private divideNumbers(text: string, divisor: number): string {
    const lines = text.split('\n');
    const euroCols = new Set<number>();

    const outLines = lines.map((line, i) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const cells = trimmed.slice(1, -1).split('|');
        const nextLine = (lines[i + 1] || '').trim();
        const isNextSep =
          nextLine.startsWith('|') &&
          nextLine.endsWith('|') &&
          nextLine
            .slice(1, -1)
            .split('|')
            .every((c) => /^[\s:-]+$/.test(c));

        // Rilevamento intestazione tabella Markdown
        if (isNextSep) {
          euroCols.clear();
          cells.forEach((cell, idx) => {
            if (/€|euro|eur\b|costo|spesa|importo|canone|ricav|quota/i.test(cell)) {
              euroCols.add(idx);
            }
          });
          return line;
        }

        // Riga di separazione (:---)
        const isCurrentSep = cells.every((c) => /^[\s:-]+$/.test(c));
        if (isCurrentSep) {
          return line;
        }

        // Riga di dati della tabella: divide solo le celle appartenenti alle colonne in €
        if (euroCols.size > 0) {
          const newCells = cells.map((cell, idx) => {
            if (euroCols.has(idx)) {
              return cell.replaceAll(this.numberRegex, (m, offset: number, fullCell: string) => {
                const after = fullCell.slice(offset + m.length);
                if (/^\s*(?:%|percento|per cento|percentuale|percent|percentage)/i.test(after)) {
                  return m;
                }
                return this.divideSingleNumber(m, divisor);
              });
            }
            return cell;
          });
          return '|' + newCells.join('|') + '|';
        }
        return line;
      } else {
        euroCols.clear();
      }

      // Testo inline al di fuori delle tabelle: divide SOLO se associato a simboli monetari (€, EUR, euro) o termini di spesa/costo
      return line.replaceAll(this.numberRegex, (match, offset: number, fullText: string) => {
        const after = fullText.slice(offset + match.length);
        // Esclusione percentuali
        if (/^\s*(?:%|percento|per cento|percentuale|percent|percentage)/i.test(after)) {
          return match;
        }

        // Presenza suffisso monetario (es. "100.000 €", "50.000 EUR", "20.000 euro", "5k€")
        const hasEuroSuffix = /^\s*(?:€|EUR|euro|[kM]€)/i.test(after);

        // Presenza prefisso monetario o contestuale economico (es. "€ 100.000", "costo di 50.000", "spesa pari a 30.000")
        const before = fullText.slice(0, offset);
        const hasEuroPrefix = /(?:€|EUR|euro)\s*$/i.test(before);
        const hasEconomicContext = this.isEconomicContext(before);

        if (hasEuroSuffix || hasEuroPrefix || hasEconomicContext) {
          return this.divideSingleNumber(match, divisor);
        }

        // Tutti gli altri numeri (es. "100 risultati", "500 server") rimangono uguali
        return match;
      });
    });

    return outLines.join('\n');
  }

  /**
   * Verifica in modo lineare (senza backtracking) se il testo prima del numero esprime un contesto economico.
   */
  private isEconomicContext(beforeText: string): boolean {
    const lower = beforeText.toLowerCase();
    const keywords = ['costo', 'spesa', 'importo', 'canone', 'ricav', 'quota'];
    for (const kw of keywords) {
      const idx = lower.lastIndexOf(kw);
      if (idx !== -1) {
        const remainder = lower.slice(idx + kw.length).trim();
        // Verifica connettivi ammessi: "totale di", "complessivo di", "pari a", "di competenza:", ecc.
        if (/^(?:totale|complessiv[ao]|di\s+competenza|competenza|di|pari\s+a|[:\s])*$/.test(remainder)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Applica la divisione a una singola stringa numerica preservando formattazione e decimali.
   */
  private divideSingleNumber(match: string, divisor: number): string {
    const parsedInfo = this.parseNumber(match);
    if (parsedInfo === null) {
      return match;
    }

    const { value, isItalian, hasDecimals, decimalPlaces } = parsedInfo;
    const activeDivisor = this.config.divisorPerNumber ? this.getRandomDivisor() : divisor;
    const divided = value / activeDivisor;

    if (hasDecimals) {
      return isItalian
        ? new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          }).format(divided)
        : divided.toFixed(decimalPlaces);
    } else {
      const rounded = Math.round(divided);
      return isItalian
        ? new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(rounded)
        : String(rounded);
    }
  }

  /**
   * Analizza la stringa numerica riconoscendo formati italiani (16.801.145,14) o standard (16801145.14 o 100).
   */
  private parseNumber(match: string): {
    value: number;
    isItalian: boolean;
    hasDecimals: boolean;
    decimalPlaces: number;
  } | null {
    let parsed: number;
    let isItalian = false;
    let hasDecimals = false;
    let decimalPlaces = 0;

    if (match.includes('.') && match.includes(',')) {
      if (match.lastIndexOf(',') > match.lastIndexOf('.')) {
        // Formato italiano completo: 16.801.145,14
        isItalian = true;
        hasDecimals = true;
        decimalPlaces = match.length - match.lastIndexOf(',') - 1;
        parsed = Number.parseFloat(match.replaceAll('.', '').replace(',', '.'));
      } else {
        // Formato anglosassone con migliaia: 16,801,145.14
        hasDecimals = true;
        decimalPlaces = match.length - match.lastIndexOf('.') - 1;
        parsed = Number.parseFloat(match.replaceAll(',', ''));
      }
    } else if (match.includes(',')) {
      // Decimale italiano senza separatore migliaia: 1234,56
      isItalian = true;
      hasDecimals = true;
      decimalPlaces = match.length - match.lastIndexOf(',') - 1;
      parsed = Number.parseFloat(match.replace(',', '.'));
    } else if (match.includes('.')) {
      const parts = match.split('.');
      // Migliaia italiane senza decimali: 16.801.145
      if (parts.length > 1 && parts.every((p, i) => i === 0 || p.length === 3)) {
        isItalian = true;
        parsed = Number.parseInt(match.replaceAll('.', ''), 10);
      } else {
        // Float standard: 123.45
        hasDecimals = true;
        decimalPlaces = match.length - match.lastIndexOf('.') - 1;
        parsed = Number.parseFloat(match);
      }
    } else {
      // Intero semplice: 100
      parsed = Number.parseInt(match, 10);
    }

    if (Number.isNaN(parsed)) {
      return null;
    }

    return { value: parsed, isItalian, hasDecimals, decimalPlaces };
  }

  /**
   * Permette di aggiungere programmaticamente nuove parole da anonimizzare.
   */
  public addWord(word: string, replacement = this.config.defaultWordReplacement): void {
    this.config.wordsToReplace[word] = replacement;
    if (!this.config.wordList.includes(word)) {
      this.config.wordList.push(word);
    }
    this.compiledDictionaryRegex = null;
    this.compiledLookup = null;
    this.compiledReverseRegex = null;
    this.compiledReverseLookup = null;
  }

  /**
   * Permette di aggiornare la lista delle parole da anonimizzare.
   */
  public setWordList(words: string[]): void {
    this.config.wordList = [...words];
    this.compiledDictionaryRegex = null;
    this.compiledLookup = null;
    this.compiledReverseRegex = null;
    this.compiledReverseLookup = null;
  }
}
